import * as _ from "lodash";
import * as Commons from "oc-commons/Commons";
import { convertStylesStringToObject } from "oc-ui/Doms";
import { Ray, Vector2, Vector3 } from "three";
import { MAufbauten } from "../../metamodell";
import { OBB } from "three/examples/jsm/math/OBB";
import { Aufbau } from "./Aufbau";
import { v2, v3 } from "oc-threejs/short3";
import { PriorityIntervals } from "oc-commons/PriorityIntervals";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import { Ray2 } from "oc-threejs/Maths2";
import * as ClosedInterval from "oc-commons/ClosedInterval";
import { Interval } from "oc-commons/ClosedInterval";
import { createGEcke, GEcke } from "./grundrissGerichteterGraph";
import * as Maths from "oc-commons/Maths";
import { Ecke } from "./Ecke";
import { epsilon } from "./constants";

export type Strecke2 = [Vector2, Vector2];
export type LengthInterval = [number, number];
export { epsilon } from "./constants";

// entspricht etwa einer Pfostenbreite. Ecken sollten nicht näher aneinander stehen können
export const pfostenbreiteCirca = 0.08;
export const epsilonSq = Math.pow(epsilon, 2);
export const parallelRadEpsilon = (0.1 * Math.PI) / 180;

/**
 * Eine Ecke des Grundriss, bildet einen ungerichteten Graphen.
 * GrundrissEcken sind durch die Punkte der Strecken oder deren Schnittpunkte definiert.
 */
export interface GrundrissEcke {
  id: number;
  punkt: Vector2;
  liegtAufGrenze: boolean;
  anliegendeKanten: GrundrissKante[];
  // zeigt aus dem Grundstück heraus
  normalSum: Vector2;
  segmentEcke?: Ecke;
}

/**
 * Eine Kante des Grundriss zwischen zwei Ecken.
 */
export interface GrundrissKante {
  id: number;
  ecken: [GrundrissEcke, GrundrissEcke];
  direction: Vector2;
  // zeigt aus dem Grundstück heraus
  normal: Vector2;
  flippedNormal: boolean;
}

export interface GrundrissGraph {
  ecken: GrundrissEcke[];
  kanten: GrundrissKante[];
}

export function kanteId(a: GrundrissEcke, b: GrundrissEcke) {
  const [idSmaller, idGreater] = a.id < b.id ? [a.id, b.id] : [b.id, a.id];
  return Maths.pairing(idSmaller, idGreater);
}

export function grundrissKanteId(kante: GrundrissKante) {
  return kante == null ? null : kanteId(...kante.ecken);
}

export function kanteZuStrecke(kante: GrundrissKante): Strecke2 {
  return kante.ecken.map((e) => e.punkt.clone()) as Strecke2;
}

/**
 * Eine Segmentkante ist die Unterteilung einer GrundrissKante anhand von Aufbauten
 */
export interface SegmentKante {
  /* entspricht dem Index im arrangement.segmente */
  index: number;
  grundrissKante: GrundrissKante;
  /* Start- und Endpunkt des Segmentes */
  punkte: Strecke2;
  /* Array mit Start- und Endpunkt von Empties, die wegen Aufbauten wie Wegen freigelassen werden sollen */
  emptyKanten: LengthInterval[];
}

export type OBBAufbau = OBB & { aufbau: Aufbau };

export interface UpdateEckenUndSegmenteFnResult {
  segmentKanten: SegmentKante[];
  mauerAufbautenObbs: OBBAufbau[];
}

export type GrundrissModus = "zeichnen" | "radieren" | "verschieben" | "selektieren";

// Interface für den `AufbautenState`, der den aktuellen Zustand der Einzäunung
// und ihrer Komponenten repräsentiert.
export interface GrundrissState {
  // Vorbedingung für ready
  pageFadedIn: boolean;

  // Ein Boolean, der angibt, ob der Zustand fertig ist oder nicht.
  ready: boolean;

  // Die Strecken, auf denen Zaunelemente stehen können
  kanten: GrundrissKante[];

  // Ein Array, das die Union aller Punkte aus `edges.points` enthält,
  // also alle Start- und Endpunkte der Segmente.
  points: Vector2[];

  modus: GrundrissModus;
}

export function updateEckenUndSegmenteFn({
  kanten,
  aufbauten,
  wegeWieMauern,
}: {
  kanten: GrundrissKante[];
  aufbauten: Aufbau[];
  wegeWieMauern: boolean;
}): UpdateEckenUndSegmenteFnResult {
  const schneideIntervalle = (
    [start, end]: [Vector3, Vector3],
    obbs: OBB[],
    invertiert: boolean
  ) => {
    const startToEndDirection = end.clone().sub(start).normalize();
    const endToStartDirection = start.clone().sub(end).normalize();
    const segmentNormal = startToEndDirection.clone().cross(v3(0, 1, 0));
    const epsilonNormal = segmentNormal.multiplyScalar(epsilon);

    /** Von Start und Ende jedes Segments werden zwei versetzte Rays geschickt um Aufbauten zu erkennen */
    const rays = [
      new Ray(start.clone().add(epsilonNormal), startToEndDirection),
      new Ray(end.clone().add(epsilonNormal), endToStartDirection),
      new Ray(start.clone().sub(epsilonNormal), startToEndDirection),
      new Ray(end.clone().sub(epsilonNormal), endToStartDirection),
    ];
    // Der Origin wird 1000 m hinter der Ecke gesetzt, damit auch die Schnittpunkte von Mauern, die hinter der Ecke liegen, erfasst werden
    rays.forEach((r) => {
      r.origin.sub(r.direction.clone().multiplyScalar(1000));
    });

    const kantenAbstandZuWorldPosition = (abstand: number): Vector3 =>
      start.clone().add(rays[0].direction.clone().multiplyScalar(abstand));

    const worldPositionZuKantenAbstand = (position) =>
      position ? position.clone().sub(start).dot(rays[0].direction) : null;

    const eckDistanceIntervalle = obbs
      .map((obb) =>
        rays
          .map((ray) => worldPositionZuKantenAbstand(obb.intersectRay(ray, v3())))
          .filter((v) => v != null)
      )
      .filter((i) => !Commons.isEmpty(i));

    const intervalle = segmentIntervallUnion(
      start.distanceTo(end),
      eckDistanceIntervalle,
      invertiert,
      0.09
    );

    const worldIntervalle = intervalle.map(
      (interval) => interval.map(kantenAbstandZuWorldPosition) as [Vector3, Vector3]
    );
    return {
      intervalle,
      worldIntervalle,
    };
  };

  // MauerAufbauten, welche die Segmente schneiden, unterteilen die Kanten in neue Kanten
  const aufbautenObbs = (elementverhalten) =>
    aufbauten
      .filter((aufbau) => aufbau.Elementverhalten == elementverhalten)
      .map((aufbau) => {
        const obb = aufbau.getOBBWorld() as OBBAufbau;
        obb.aufbau = aufbau;
        return obb;
      });

  const leerraumAufbautenObbs = aufbautenObbs("Leerraum");
  const mauerAufbautenObbs = (wegeWieMauern ? leerraumAufbautenObbs : []).concat(
    aufbautenObbs("Mauer")
  );

  let index = 0;
  const segmentKanten: SegmentKante[] = kanten.flatMap((kante) => {
    return schneideIntervalle(
      kante.ecken.map((e) => Maths3.v2To3xz(e.punkt)) as any,
      mauerAufbautenObbs,
      true
    ).worldIntervalle.map((punkte) => ({
      /* entspricht dem Index im arrangement.segmente */
      index: index++,
      grundrissKante: kante,
      punkte: punkte.map((p) => Maths3.v3To2xz(p)) as Strecke2,
      emptyKanten: schneideIntervalle(punkte, leerraumAufbautenObbs, false).intervalle,
    }));
  });

  const result: UpdateEckenUndSegmenteFnResult = {
    segmentKanten,
    mauerAufbautenObbs,
  };

  return result;
}

export function segmentIntervallUnion(
  segmentLength: number,
  /* Intervalle als Start- und End x-Position */
  intervalle: number[][],
  /* sollen die vereinigten Intervalle oder das Komplement zurückgegeben werden? */
  invertiert = false,
  /* Epsilon für die Intervall-Verschmelzung und minimale Intervall-Länge */
  epsilon = 0
) {
  const intervals = new PriorityIntervals();
  intervals.add(0, segmentLength, 0);
  intervalle.forEach((i) => intervals.add(i[0], i[1], 1));
  intervals.partition(epsilon).finite();
  const zielTyp = invertiert ? 0 : 1;
  return intervals.list
    .filter((i) => i[2] === zielTyp)
    .map(
      (interval) =>
        // typ-infos entfernen
        interval.slice(0, 2) as [number, number]
    );
}

export function teilStyle(aufbauMeta: MAufbauten) {
  const { TextFarbe2d, Farbe2d, Textur2d, css } = aufbauMeta;
  const style = {
    color: TextFarbe2d?.getStyle(),
    backgroundColor: Farbe2d?.getStyle(),
    backgroundImage: Textur2d ? `url(${Textur2d})` : undefined,
    ...convertStylesStringToObject(css),
  };
  return style;
}

export function normalToKante(linie: [Vector2, Vector2], middlePoint: Vector2) {
  return Maths2.calculateNormalTowardsC(linie[0], linie[1], middlePoint).negate();
}

export function anliegendeKanten(kante: GrundrissKante) {
  return Commons.unique(kante.ecken.flatMap((e) => e.anliegendeKanten));
}

export function achsenKanten(kante: GrundrissKante) {
  return anliegendeKanten(kante).filter(
    (k) =>
      Maths2.isParallel(kante.direction, k.direction) ||
      Maths2.isOrthogonal(kante.direction, k.direction)
  );
}

export function changeStreckenLaenge(
  kanten: GrundrissKante[],
  kantenIdToChangeCount: Map<number, number>,
  kantenId: number,
  neueLaenge: number
) {
  const zuverlängerndeKante = kanten.find((kante) => kante.id == kantenId);

  const initialeLänge = Maths2.distance(
    zuverlängerndeKante.ecken.map((e) => e.punkt) as [Vector2, Vector2]
  );
  if (Math.abs(initialeLänge - neueLaenge) < 0.001) {
    return;
  }

  interface Cluster {
    anzahlKanten: number;
    maxChangecount: number;
    orthogonaleEcken: Set<GrundrissEcke>;
    basisEcke: GrundrissEcke;
  }

  // sammle zu jeder Ecke die orthogonal zur Basisstrecke dranhängenden Ecken als Cluster
  // Ziel ist es, den Cluster zu finden, der am wenigsten Änderungen bedeuten würde.
  // Dazu wird die Anzahl der Kanten jedes Clusters aufsummiert.
  // Noch wichtiger ist es allerdings, den Cluster, in dem zuletzt eine Kante vom User editiert
  // wurde und die sich verändern würde, höher zu bewerten, damit keine Änderungs-Schleifen auftreten,
  // weil zwei geänderte Maße sich gegenseitig beeinflussen.
  const [a, b] = zuverlängerndeKante.ecken.map((basisEcke: GrundrissEcke): Cluster => {
    let anzahlKanten = 0;
    let maxChangecount = 0;
    const zuVerschiebendeEcken = new Set<GrundrissEcke>();
    zuVerschiebendeEcken.add(basisEcke);
    const visited = new Set<GrundrissKante>();
    visited.add(zuverlängerndeKante);

    Commons.graphDFS(basisEcke, (e: GrundrissEcke, hops: number) => {
      const orthogonaleEckenLokal = new Set<GrundrissEcke>();
      e.anliegendeKanten.forEach((k) => {
        if (visited.has(k)) {
          return;
        }
        visited.add(k);
        const orthogonal = Maths2.isOrthogonal(zuverlängerndeKante.direction, k.direction);
        anzahlKanten++;
        if (orthogonal) {
          k.ecken.forEach((ee) => orthogonaleEckenLokal.add(ee));
        } else {
          const changeCount = kantenIdToChangeCount.get(k.id) ?? 0;
          maxChangecount = Math.max(maxChangecount, changeCount);
        }
      });
      zuVerschiebendeEcken.addAll(orthogonaleEckenLokal);
      return orthogonaleEckenLokal;
    });

    return {
      anzahlKanten,
      maxChangecount,
      orthogonaleEcken: zuVerschiebendeEcken,
      basisEcke,
    };
  });

  // ermittle die zu verschiebenden Cluster anhand der Gewichtung
  const ordnung = a.maxChangecount - b.maxChangecount || a.anzahlKanten - b.anzahlKanten;
  const clusters: Cluster[] = [];
  if (ordnung === 0) {
    clusters.push(a);
    clusters.push(b);
  } else {
    const c = ordnung < 0 ? a : b;
    clusters.push(c);
  }

  // verteile deltaLaenge unter den ein bis zwei Clustern
  // schiebe jeden Cluster entsprechend der Ursprungsecke nach links oder rechts
  const strecke = kanteZuStrecke(zuverlängerndeKante);
  const direction = Maths2.getDirection(...strecke);
  const alteLaenge = strecke[0].distanceTo(strecke[1]);
  const deltaLaenge = (alteLaenge - neueLaenge) / clusters.length;
  const middle = Maths2.middle(strecke);
  // console.log({ clusters });
  for (const cluster of clusters) {
    const sign = -Math.sign(v2().subVectors(cluster.basisEcke.punkt, middle).dot(direction));
    const delta = direction.clone().multiplyScalar(sign * deltaLaenge);
    // console.log({ cluster, sign, delta });
    for (const ecke of cluster.orthogonaleEcken) {
      ecke.punkt.add(delta);
    }
  }

  // zähle den Changecount hoch
  const maxChangeCount = Math.max(0, ...kantenIdToChangeCount.values());
  kantenIdToChangeCount.set(kantenId, maxChangeCount + 1);
  // console.log(maxChangeCount, kantenIdToChangeCount);
}

/**
 * Entfernt Teilstücke (oder ganze Kanten), die von einem "Radierer" (Linie + Radius)
 * getroffen werden, und löscht verwaiste Ecken (ohne anliegende Kanten).
 *
 * @param ecken   Array aller GrundrissEcke (wird mutabel bearbeitet)
 * @param kanten  Array aller GrundrissKante (wird mutabel bearbeitet)
 * @param radierer Objekt mit `line: Vector2[]` (2-Punkt-Linie) und `radius: number`
 */
export function removeFromStrecken(
  ecken: GrundrissEcke[],
  kanten: GrundrissKante[],
  radierer: { line: Vector2[]; radius: number }
): void {
  const minLength = 0.5; // Unterhalb dieser Länge wird ein Teilsegment verworfen

  // Hilfsfunktion, um ggf. bestehende Ecken wiederzuverwenden, falls
  // der Punkt nahe genug an einer bereits existierenden Ecke liegt
  const cachedEcke = cachedEckeEpsilon(ecken, (p) => createEcke(Commons.createId(), p));

  const edgesToRemove: GrundrissKante[] = [];
  const edgesToAdd: GrundrissKante[] = [];

  // 1) Alle Kanten auf Schnitt mit dem "Radierer" prüfen
  for (const kante of kanten) {
    const seg: Strecke2 = kanteZuStrecke(kante);
    const intersections = Maths2.lineLineDistancedIntersections(seg, radierer, true);
    // Wir schneiden nur, wenn genau zwei Schnittpunkte gefunden werden
    if (intersections.length !== 2) {
      continue;
    }

    // 2) Teilintervalle berechnen, die nach dem "Ausradieren" übrig bleiben
    const ray = new Ray2().setFromPoints(seg);
    const segInterval = seg.map((p) => ray.distanceOriginToPointProjection(p)) as Interval;
    const removeInterval = intersections.map((p) =>
      ray.distanceOriginToPointProjection(p)
    ) as Interval;
    let neueIntervalle = ClosedInterval.subtract([segInterval], removeInterval) as Interval[];
    neueIntervalle = neueIntervalle.filter((i) => ClosedInterval.size(i) > minLength);

    // Alte Kante kommt weg
    edgesToRemove.push(kante);

    // 3) Neue Teil-Kanten erzeugen
    for (const interval of neueIntervalle) {
      const ecken = interval.map((t) => cachedEcke(ray.at(t))) as [GrundrissEcke, GrundrissEcke];
      const newKante: GrundrissKante = createKante(...ecken);
      edgesToAdd.push(newKante);
    }
  }

  // 4) Alte Kanten entfernen
  for (const k of edgesToRemove) {
    // Kante bei den Ecken austragen
    k.ecken.forEach((e) => (e.anliegendeKanten = e.anliegendeKanten.filter((ka) => ka !== k)));
    // Und auch aus dem Kanten-Array löschen
    const idx = kanten.indexOf(k);
    if (idx >= 0) {
      kanten.splice(idx, 1);
    }
  }

  // 5) Neue Kanten eintragen
  for (const k of edgesToAdd) {
    kanten.push(k);
    k.ecken.forEach((e) => e.anliegendeKanten.push(k));
  }

  // 6) Verwaiste Ecken entfernen (Ecken ohne anliegende Kanten)
  for (let i = ecken.length - 1; i >= 0; i--) {
    if (ecken[i].anliegendeKanten.length === 0) {
      ecken.splice(i, 1);
    }
  }

  if (edgesToAdd.length > 0 || edgesToRemove.length > 0) {
    APP.logUserAction({ action: "radieren" }, { cached: true });
  }
}

export function findNearestUnder<T>(
  elements: T[],
  mapToNumer: (T) => number,
  minNumber: number
): T {
  return _.minBy(
    elements.filter((e) => mapToNumer(e) <= minNumber),
    mapToNumer
  );
}

/**
 * Gibt den Winkel eines Vector2 im Grundriss zurück,
 * so dass er auch folgendem World-Quaternion entspricht:
 * quaternion.setFromAxisAngle(Maths3.vector.y, this.angle);
 *
 * Die Winkel-Orientierung ist dann so:
 *
 *           90°
 *  180° <---|---> 0°
 *          270°
 *
 * @param vector2
 * @returns Winkel des Vectors zur positiven X-Achse
 */
export function v2ToAngle(vector2: Vector2): number {
  return v2(vector2.x, vector2.y).angle();
}

export function createEcke(id: number, punkt: Vector2): GrundrissEcke {
  return {
    id: id,
    punkt: punkt.clone(),
    liegtAufGrenze: false,
    anliegendeKanten: [],
    normalSum: v2(),
  };
}

export function createKante(ecke1: GrundrissEcke, ecke2: GrundrissEcke): GrundrissKante {
  return {
    id: kanteId(ecke1, ecke2),
    ecken: [ecke1, ecke2] as [GrundrissEcke, GrundrissEcke],
    direction: Maths2.getDirection(ecke1.punkt, ecke2.punkt),
    normal: null,
    flippedNormal: false,
  };
}

export function cachedKanten(kanten: GrundrissKante[]) {
  const idToKantenMap = new Map<number, GrundrissKante>();
  return (e1: GrundrissEcke, e2: GrundrissEcke) => {
    const id = kanteId(e1, e2);
    let kante = idToKantenMap.get(id);
    if (kante == null) {
      kante = createKante(e1, e2);
      kanten.push(kante);
    }
    return kante;
  };
}

interface MiniGraph {
  punkt: Vector2;
}

export const cachedEckeEpsilon =
  <T extends MiniGraph>(ecken: T[], createEcke: (p: Vector2) => T) =>
  (p: Vector2) => {
    let ecke = ecken.find((e) => e.punkt.distanceToSquared(p) < epsilonSq);
    if (ecke == null) {
      ecke = createEcke(p);
      ecken.push(ecke);
    }
    return ecke;
  };

/**
 * ACHTUNG: Der erzeugte Graph hat komplett neue IDs
 * @param strecken
 */
export function streckenZuGraph(strecken: Strecke2[]): GrundrissGraph {
  // Erzeuge ein eindeutiges Array aller GrundrissEcke (ohne Duplikate) und Kanten
  const ecken: GrundrissEcke[] = [];
  const cachedEcke = cachedEckeEpsilon(ecken, (p) => createEcke(Commons.createId(), p));
  const kanten: GrundrissKante[] = [];
  const cachedKante = cachedKanten(kanten);
  for (const [p1, p2] of strecken) {
    const [g1, g2] = [p1, p2].map(cachedEcke);
    const kante = cachedKante(g1, g2);
    g1.anliegendeKanten.push(kante);
    g2.anliegendeKanten.push(kante);
  }
  return { ecken, kanten };
}

export function kantenZuStrecken(kanten: GrundrissKante[]): Strecke2[] {
  return kanten.map(kanteZuStrecke);
}

export function ungerichterZuGerichterGraph(grundrissEcken: GrundrissEcke[]): GEcke[] {
  const ecken2gEckenMap = new Map<GrundrissEcke, GEcke>();
  const gEcken: GEcke[] = grundrissEcken.map((ecke) => {
    const gecke: GEcke = createGEcke(ecke.id, ecke.punkt.clone());
    ecken2gEckenMap.set(ecke, gecke);
    return gecke;
  });
  for (const ecke of grundrissEcken) {
    for (const kante of ecke.anliegendeKanten) {
      const [a, b] = kante.ecken.map((e) => ecken2gEckenMap.get(e));
      a.adjazenz.add(b);
    }
  }
  return gEcken;
}

export function gerichterZuUngerichtemGraph(gEcken: GEcke[]): GrundrissGraph {
  const gecken2EckenMap = new Map<GEcke, GrundrissEcke>();
  const ecken: GrundrissEcke[] = gEcken.map((gecke) => {
    const ecke = createEcke(gecke.id, gecke.punkt);
    gecken2EckenMap.set(gecke, ecke);
    return ecke;
  });
  const kanten: GrundrissKante[] = [];
  const cachedKante = cachedKanten(kanten);
  for (const a of gEcken) {
    for (const b of a.adjazenz) {
      const [g1, g2] = [a, b].map((e) => gecken2EckenMap.get(e) as GrundrissEcke);
      const kante = cachedKante(g1, g2);
      g1.anliegendeKanten.push(kante);
      g2.anliegendeKanten.push(kante);
    }
  }
  return { ecken, kanten };
}

export function kantenZuEcken(kanten: GrundrissKante[]): GrundrissEcke[] {
  const ecken = new Set<GrundrissEcke>();
  for (const kante of kanten) {
    kante.ecken.forEach((ecke) => ecken.add(ecke));
  }
  return ecken.toArray();
}

export function unmarshalGraph(
  ecken_: { id: number; punkt: Vector2 }[],
  kanten_: { ecken: [number, number] }[]
): GrundrissGraph {
  const gecken2EckenMap = new Map<number, GrundrissEcke>();
  const ecken: GrundrissEcke[] = ecken_.map(({ id, punkt }) => {
    const ecke = createEcke(id, punkt);
    gecken2EckenMap.set(id, ecke);
    return ecke;
  });
  const kanten: GrundrissKante[] = [];
  const cachedKante = cachedKanten(kanten);
  for (const { ecken } of kanten_) {
    const [g1, g2] = ecken.map((e) => gecken2EckenMap.get(e) as GrundrissEcke);
    const kante = cachedKante(g1, g2);
    g1.anliegendeKanten.push(kante);
    g2.anliegendeKanten.push(kante);
  }
  return { ecken, kanten };
}

export function getGrundrissKanteX(segmentKante: SegmentKante, segmentX: number): number {
  const { grundrissKante } = segmentKante;
  const kantenDir = grundrissKante.direction;
  const segmentDir = Maths2.getDirection(...segmentKante.punkte);
  const point = segmentKante.punkte[0].clone().addScaledVector(segmentDir, segmentX);
  const diff = point.clone().sub(grundrissKante.ecken[0].punkt);
  return diff.dot(kantenDir);
}

export function getSegmentKanteX(segmentKante: SegmentKante, point: Vector2): number {
  const segmentDir = Maths2.getDirection(...segmentKante.punkte);
  const diff = point.clone().sub(segmentKante.punkte[0]);
  return diff.dot(segmentDir);
}
